import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { Button } from 'ncoded-component-library';
import classNames from 'classnames';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import SortIcon from 'icons/Sort.icon';
import ResolvePopup from 'components/ResolvePopup';
import { getIsDesc, getIsSortApplied, getSortValue } from 'utils';

import './SortingButton.styles.scss';

type SortingButtonProps = {
  className?: string;
  content?: ReactNode;
  sortingValue: string;
  popupContent?: string;
  disabled?: boolean;
  sort?: string;
  ascDefault?: boolean; // smallest value first
  updateSort: (sortValue: string) => void;
};

const SortingButton: FC<SortingButtonProps> = ({
  className,
  content,
  sortingValue,
  popupContent,
  sort,
  disabled,
  ascDefault = true,
  updateSort,
}) => {
  const isDesc = getIsDesc(sort);

  const isSelected = getIsSortApplied(sort, sortingValue);

  const handleUpdateSort = useCallback(
    () =>
      updateSort(
        getSortValue(sortingValue, isSelected ? !isDesc : !ascDefault),
      ),
    [ascDefault, isDesc, isSelected, sortingValue, updateSort],
  );

  const sortContent = useMemo(() => {
    if (!sortingValue) return content;

    return (
      <>
        <Button
          disabled={disabled}
          onClick={handleUpdateSort}
          className="sortable-button--content"
        >
          {content}
        </Button>

        <Button
          disabled={disabled}
          onClick={handleUpdateSort}
          className={classNames('sortable-button--icons', {
            selected: isSelected,
            rotated: !isDesc,
          })}
        >
          {isSelected ? (
            <ArrowdownIcon />
          ) : (
            <>
              <SortIcon />
              <SortIcon />
            </>
          )}
        </Button>
      </>
    );
  }, [content, sortingValue, isSelected, isDesc, disabled, handleUpdateSort]);

  return (
    <div
      className={classNames('sortable-button', className, {
        selected: isSelected,
      })}
    >
      {popupContent ? (
        <ResolvePopup popupContent={popupContent} onHover preferUpwards>
          {sortContent}
        </ResolvePopup>
      ) : (
        sortContent
      )}
    </div>
  );
};

export default SortingButton;
